// ---------------------------------------------------------------------------
// Page Builder API Hooks — mounted pages contract only
// ---------------------------------------------------------------------------
// No legacy /pages/sites dependency. Pages list/detail/save/publish all align
// to the authenticated /crm/pages/* proxy and its versioned editor model.
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface PageSummary {
  id: string;
  title: string;
  slug: string;
  status: "draft" | "published" | "archived";
  pageType?: string;
  siteId: string | null;
  updatedAt: string;
  views: number | null;
  publishedUrl: string | null;
  scheduledPublishAt: string | null;
  scheduledPublishVersionId: string | null;
  settings: Record<string, unknown>;
  reuseKind: "personal_template" | "page_instance" | null;
}

interface PageTelemetrySummary {
  pageId: string;
  publishedVersionId: string | null;
  pageViews: number;
  pageViewsToday: number;
  directConversions: number;
  directConversionsToday: number;
}

export interface PageDetail {
  id: string;
  title: string;
  slug: string;
  status: "draft" | "published" | "archived";
  pageType: string;
  siteId: string | null;
  updatedAt: string;
  publishedUrl: string | null;
  scheduledPublishAt: string | null;
  scheduledPublishVersionId: string | null;
  currentVersionId: string | null;
  publishedVersionId: string | null;
  version: number;
  content: unknown;
  seoMeta: Record<string, unknown>;
  settings: Record<string, unknown>;
  reuseKind: "personal_template" | "page_instance" | null;
}

export interface PageVersionSummary {
  id: string;
  pageId: string;
  versionNumber: number;
  puckData: unknown;
  thumbnail: string | null;
  generationSource: "ai" | "manual" | "duplicate" | "autosave";
  aiPrompt: string | null;
  createdBy: string;
  createdAt: string;
}

export interface PublishPageResult {
  pageId: string;
  versionId: string;
  publishedUrl: string;
  threats?: string[];
  accessibilityAudit?: PublishAccessibilityAuditResult;
}

export interface SchedulePagePublishResult {
  pageId: string;
  scheduledPublishAt: string | null;
  scheduledPublishVersionId: string | null;
  version: number;
}

export interface PreviewPageResult {
  pageId: string;
  versionId: string;
  previewPath: string;
  previewUrl: string;
  previewToken: string;
  expiresAt: string;
  threats?: string[];
  scheduledPublishGap?: string;
  accessibilityAudit?: PublishAccessibilityAuditResult;
}

export interface PublishAccessibilityAuditIssue {
  id: string;
  impact: "critical" | "serious" | "moderate" | "minor" | null;
  help: string;
  helpUrl: string;
  targets: string[];
  nodeCount: number;
  summary: string | null;
}

export interface PublishAccessibilityAuditResult {
  source: "axe-core";
  auditedAt: string;
  status: "pass" | "warn" | "block";
  summary: {
    violationCount: number;
    criticalCount: number;
    seriousCount: number;
    moderateCount: number;
    minorCount: number;
  };
  issues: PublishAccessibilityAuditIssue[];
}

interface PublishRequestInput {
  accessibilityAudit?: PublishAccessibilityAuditResult;
}

interface PageRecord {
  id: string;
  title: string;
  slug: string;
  status: "draft" | "published" | "archived";
  pageType?: string | null;
  siteId: string | null;
  updatedAt: string;
  publishedUrl: string | null;
  scheduledPublishAt?: string | null;
  scheduledPublishVersionId?: string | null;
  currentVersionId: string | null;
  publishedVersionId?: string | null;
  version?: number;
  seoMeta?: Record<string, unknown> | null;
  settings?: Record<string, unknown> | null;
}

interface PageVersionRecord {
  id: string;
  pageId: string;
  versionNumber: number;
  puckData: unknown;
  thumbnail?: string | null;
  generationSource?: "ai" | "manual" | "duplicate" | "autosave";
  aiPrompt?: string | null;
  createdBy?: string;
  createdAt?: string;
}

interface PageListEnvelope {
  items?: PageRecord[];
}

const EMPTY_PAGE_DATA = {
  content: [],
  root: {},
};

type DuplicateMode = "page_copy" | "personal_template" | "template_apply";

function resolveReuseKind(settings: Record<string, unknown> | null | undefined): PageSummary["reuseKind"] {
  const reuse = settings?.reuse;
  if (!reuse || typeof reuse !== "object") {
    return null;
  }

  const kind = (reuse as Record<string, unknown>).kind;
  if (kind === "personal_template" || kind === "page_instance") {
    return kind;
  }

  return null;
}

function slugifyTitle(title: string): string {
  const normalized = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || "untitled-page";
}

function normalizePageSummary(page: PageRecord): PageSummary {
  return {
    id: page.id,
    title: page.title,
    slug: page.slug,
    status: page.status,
    pageType: page.pageType ?? "custom",
    siteId: page.siteId,
    updatedAt: page.updatedAt,
    views: null,
    publishedUrl: page.publishedUrl,
    scheduledPublishAt: page.scheduledPublishAt ?? null,
    scheduledPublishVersionId: page.scheduledPublishVersionId ?? null,
    settings: page.settings ?? {},
    reuseKind: resolveReuseKind(page.settings),
  };
}

async function fetchPageTelemetrySummary(pageIds: string[]): Promise<Map<string, PageTelemetrySummary>> {
  if (pageIds.length === 0) {
    return new Map();
  }

  const response = await apiFetch<{ data: PageTelemetrySummary[] }>(
    `/crm/pages/health-summary?pageIds=${encodeURIComponent(pageIds.join(","))}`,
  );

  return new Map(
    (response.data ?? []).map((summary) => [summary.pageId, summary]),
  );
}

function normalizePageDetail(page: PageRecord, versions: PageVersionRecord[]): PageDetail {
  const activeVersion =
    versions.find((version) => version.id === page.currentVersionId)
    ?? versions[0]
    ?? null;

  return {
    id: page.id,
    title: page.title,
    slug: page.slug,
    status: page.status,
    pageType: page.pageType ?? "custom",
    siteId: page.siteId,
    updatedAt: page.updatedAt,
    publishedUrl: page.publishedUrl,
    scheduledPublishAt: page.scheduledPublishAt ?? null,
    scheduledPublishVersionId: page.scheduledPublishVersionId ?? null,
    currentVersionId: page.currentVersionId,
    publishedVersionId: page.publishedVersionId ?? null,
    version: page.version ?? 1,
    content: activeVersion?.puckData ?? EMPTY_PAGE_DATA,
    seoMeta: page.seoMeta ?? {},
    settings: page.settings ?? {},
    reuseKind: resolveReuseKind(page.settings),
  };
}

function normalizePageVersion(version: PageVersionRecord): PageVersionSummary {
  return {
    id: version.id,
    pageId: version.pageId,
    versionNumber: version.versionNumber,
    puckData: version.puckData,
    thumbnail: version.thumbnail ?? null,
    generationSource: version.generationSource ?? "manual",
    aiPrompt: version.aiPrompt ?? null,
    createdBy: version.createdBy ?? "unknown",
    createdAt: version.createdAt ?? new Date(0).toISOString(),
  };
}

function extractPageItems(data: PageListEnvelope | PageRecord[] | null | undefined): PageRecord[] {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === "object" && Array.isArray(data.items)) {
    return data.items;
  }

  return [];
}

export async function fetchPageList(): Promise<PageSummary[]> {
  const response = await apiFetch<{ data: PageListEnvelope | PageRecord[] | null }>("/crm/pages?limit=100");
  const pageItems = extractPageItems(response.data);
  const normalizedPages = pageItems.map(normalizePageSummary);

  try {
    const telemetryByPageId = await fetchPageTelemetrySummary(normalizedPages.map((page) => page.id));
    return normalizedPages.map((page) => ({
      ...page,
      views: telemetryByPageId.get(page.id)?.pageViews ?? 0,
    }));
  } catch {
    // Keep the UI honest when telemetry recovery lags behind the mounted page list.
    return normalizedPages;
  }
}

export async function fetchPageDetail(pageId: string): Promise<PageDetail> {
  const [pageResponse, versionsResponse] = await Promise.all([
    apiFetch<{ data: PageRecord }>(`/crm/pages/${pageId}`),
    apiFetch<{ data: { items: PageVersionRecord[] } }>(`/crm/pages/${pageId}/versions?limit=50`),
  ]);

  return normalizePageDetail(pageResponse.data, versionsResponse.data.items ?? []);
}

export async function fetchPageVersions(pageId: string): Promise<PageVersionSummary[]> {
  const response = await apiFetch<{ data: { items: PageVersionRecord[] } }>(`/crm/pages/${pageId}/versions?limit=50`);
  return (response.data.items ?? []).map(normalizePageVersion);
}

export function usePageList() {
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPages = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setPages(await fetchPageList());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Page inventory is temporarily unavailable. Site containers can still load while `/crm/pages` recovers.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPages();
  }, [fetchPages]);

  return { pages, isLoading, error, refetch: fetchPages };
}

export function usePageDetail(pageId: string | undefined) {
  const [page, setPage] = useState<PageDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchDetail = useCallback(async () => {
    if (!pageId || pageId === "new") {
      setPage(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      setPage(await fetchPageDetail(pageId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load page");
    } finally {
      setIsLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  return { page, isLoading, error, refetch: fetchDetail };
}

export async function createPage(data: {
  title: string;
  slug?: string;
  siteId?: string;
  content?: unknown;
  settings?: Record<string, unknown>;
  seoMeta?: Record<string, unknown>;
}): Promise<PageDetail> {
  const createResponse = await apiFetch<{ data: PageRecord }>("/crm/pages", {
    method: "POST",
    body: JSON.stringify({
      title: data.title,
      slug: data.slug ?? slugifyTitle(data.title),
      siteId: data.siteId,
      settings: data.settings,
      seoMeta: data.seoMeta,
    }),
  });

  const page = createResponse.data;
  const initialContent = data.content ?? EMPTY_PAGE_DATA;

  await savePage(page.id, initialContent);
  return fetchPageDetail(page.id);
}

export async function duplicatePage(input: {
  pageId: string;
  title: string;
  mode?: DuplicateMode;
}): Promise<PageDetail> {
  const slugBase = slugifyTitle(input.title);
  const slugSuffix = new Date().getTime().toString(36).slice(-6);
  const response = await apiFetch<{ data: { page: PageRecord } }>(`/crm/pages/${input.pageId}/duplicate`, {
    method: "POST",
    body: JSON.stringify({
      slug: `${slugBase}-${slugSuffix}`,
      title: input.title,
      mode: input.mode ?? "page_copy",
    }),
  });

  return fetchPageDetail(response.data.page.id);
}

export async function cloneAndAdaptPage(input: {
  pageId: string;
  name: string;
  slug: string;
}): Promise<PageDetail> {
  const response = await apiFetch<{ data: { page: PageRecord } }>(`/crm/pages/${input.pageId}/clone-and-adapt`, {
    method: "POST",
    body: JSON.stringify({
      name: input.name,
      slug: input.slug,
    }),
  });

  return fetchPageDetail(response.data.page.id);
}

export function isPersonalTemplatePage(page: Pick<PageSummary, "reuseKind">): boolean {
  return page.reuseKind === "personal_template";
}

export async function savePage(
  pageId: string,
  content: unknown,
  generationSource: PageVersionSummary["generationSource"] = "manual",
): Promise<void> {
  await apiFetch(`/crm/pages/${pageId}/versions`, {
    method: "POST",
    body: JSON.stringify({
      puckData: content,
      generationSource,
    }),
  });
}

export async function updatePageSettings(
  pageId: string,
  expectedVersion: number,
  settings: Record<string, unknown>,
): Promise<PageDetail> {
  return updatePageMetadata(pageId, expectedVersion, { settings });
}

export async function updatePageMetadata(
  pageId: string,
  expectedVersion: number,
  input: {
    title?: string;
    slug?: string;
    settings?: Record<string, unknown>;
    seoMeta?: Record<string, unknown>;
  },
): Promise<PageDetail> {
  const response = await apiFetch<{ data: PageRecord }>(`/crm/pages/${pageId}`, {
    method: "PUT",
    body: JSON.stringify({
      expectedVersion,
      ...input,
    }),
  });

  return fetchPageDetail(response.data.id);
}

export async function restorePageVersion(pageId: string, versionId: string): Promise<void> {
  await apiFetch(`/crm/pages/${pageId}/restore/${versionId}`, {
    method: "POST",
  });
}

export function usePageVersions(pageId: string | undefined) {
  const [versions, setVersions] = useState<PageVersionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVersions = useCallback(async () => {
    if (!pageId || pageId === "new") {
      setVersions([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      setVersions(await fetchPageVersions(pageId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load version history");
    } finally {
      setIsLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    void fetchVersions();
  }, [fetchVersions]);

  return { versions, isLoading, error, refetch: fetchVersions };
}

export async function publishPage(
  pageId: string,
  input?: PublishRequestInput,
): Promise<PublishPageResult> {
  const response = await apiFetch<{ data: PublishPageResult }>(`/crm/pages/publish/${pageId}`, {
    method: "POST",
    body: JSON.stringify(input ?? {}),
  });
  return response.data;
}

export async function createPagePreview(
  pageId: string,
  versionId?: string,
  input?: PublishRequestInput,
): Promise<PreviewPageResult> {
  const response = await apiFetch<{ data: PreviewPageResult }>(`/crm/pages/publish/${pageId}/preview`, {
    method: "POST",
    body: JSON.stringify({
      ...(versionId ? { versionId } : {}),
      ...(input?.accessibilityAudit ? { accessibilityAudit: input.accessibilityAudit } : {}),
    }),
  });
  return response.data;
}

export async function schedulePagePublish(
  pageId: string,
  input: {
    expectedVersion: number;
    publishAt: string;
    versionId?: string;
  },
): Promise<SchedulePagePublishResult> {
  const response = await apiFetch<{ data: SchedulePagePublishResult }>(`/crm/pages/publish/${pageId}/schedule`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function cancelScheduledPagePublish(
  pageId: string,
  expectedVersion: number,
): Promise<SchedulePagePublishResult> {
  const response = await apiFetch<{ data: SchedulePagePublishResult }>(`/crm/pages/publish/${pageId}/schedule`, {
    method: "DELETE",
    body: JSON.stringify({ expectedVersion }),
  });
  return response.data;
}

export function useAutoSave(pageId: string | undefined, content: unknown) {
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastContentRef = useRef<string>("");
  const lastPageIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!pageId || pageId === "new") return;

    const contentStr = JSON.stringify(content);

    if (pageId !== lastPageIdRef.current) {
      lastPageIdRef.current = pageId;
      lastContentRef.current = contentStr;
      setSaveStatus("idle");
      return;
    }

    if (contentStr === lastContentRef.current) return;
    lastContentRef.current = contentStr;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);

    setSaveStatus("idle");
    timeoutRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await savePage(pageId, content, "autosave");
        setSaveStatus("saved");
        resetTimerRef.current = setTimeout(() => setSaveStatus("idle"), 3000);
      } catch {
        setSaveStatus("error");
      }
    }, 2000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, [pageId, content]);

  return saveStatus;
}

export const __pagesHookInternals = {
  EMPTY_PAGE_DATA,
  slugifyTitle,
  normalizePageSummary,
  normalizePageDetail,
  resolveReuseKind,
  isPersonalTemplatePage,
};
